import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { businessService } from '../../../services/businessService';
import { subscriptionService } from '../../../services/subscriptionService';
import { messagingService } from '../../../services/messagingService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Image from '../../../components/AppImage';
import StarRating from '../../../components/ui/StarRating';

const EditableBusinessListing = ({ 
  business = null, 
  onSave, 
  onCancel, 
  isEditing = false, 
  onToggleEdit
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [isEditable, setIsEditable] = useState(isEditing);
  const [isSaving, setIsSaving] = useState(false);
  const [editedBusiness, setEditedBusiness] = useState(business || {});
  const [expandedServices, setExpandedServices] = useState(new Set());
  const [editingService, setEditingService] = useState(null);
  const [services, setServices] = useState([]);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [businessHours, setBusinessHours] = useState([]);
  const [showMap, setShowMap] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [newService, setNewService] = useState({
    name: '',
    description: '',
    price: '',
    duration_minutes: '',
    is_in_person: true,
    is_online: false,
    max_participants: 1
  });

  // Load business services and subscription data
  useEffect(() => {
    const loadBusinessData = async () => {
      if (!business?.id) return;

      try {
        // Load services for this business
        const { data: servicesData, error: servicesError } = await businessService?.getBusinessServices(business?.id);
        if (!servicesError && servicesData) {
          setServices(servicesData);
        }

        // Load business hours
        const { data: hoursData, error: hoursError } = await businessService?.getBusinessHours(business?.id);
        if (!hoursError && hoursData) {
          setBusinessHours(hoursData);
        }

        // Load subscription data
        const subscription = await subscriptionService?.getBusinessSubscription(business?.id);
        setSubscriptionData(subscription);
      } catch (error) {
        console.error('Error loading business data:', error);
      }
    };

    loadBusinessData();
  }, [business?.id]);

  useEffect(() => {
    if (business) {
      setEditedBusiness({ ...business });
    }
  }, [business]);

  // New helper function to check if business has Google My Business integration
  const hasGoogleMyBusiness = () => {
    return editedBusiness?.google_my_business_id || 
           editedBusiness?.google_places_id || 
           editedBusiness?.google_my_business_url;
  };

  const handleToggleEdit = () => {
    const newEditState = !isEditable;
    
    if (onToggleEdit) {
      onToggleEdit(newEditState);
    }
    
    setIsEditable(newEditState);
    
    // Reset to original business data when cancelling edit
    if (!newEditState && business) {
      setEditedBusiness({ ...business });
    }
  };

  const handleInputChange = (field, value) => {
    setEditedBusiness(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!user?.id || !editedBusiness?.id) {
      console.error('Missing required data for save operation');
      return;
    }

    try {
      setIsSaving(true);
      
      // Use business service to update the business - include new Google My Business fields
      const { data, error } = await businessService?.updateBusiness(
        editedBusiness?.id,
        {
          name: editedBusiness?.name,
          short_description: editedBusiness?.short_description,
          description: editedBusiness?.description,
          phone: editedBusiness?.phone,
          email: editedBusiness?.email,
          website_url: editedBusiness?.website_url,
          address_line1: editedBusiness?.address_line1,
          address_line2: editedBusiness?.address_line2,
          city: editedBusiness?.city,
          county: editedBusiness?.county,
          postcode: editedBusiness?.postcode,
          latitude: editedBusiness?.latitude,
          longitude: editedBusiness?.longitude,
          // Add Google My Business fields
          google_my_business_id: editedBusiness?.google_my_business_id,
          google_my_business_url: editedBusiness?.google_my_business_url,
          google_places_id: editedBusiness?.google_places_id,
          google_sync_enabled: editedBusiness?.google_sync_enabled
        }
      );

      if (!error) {
        // Save business hours if they have changed
        if (businessHours?.length > 0) {
          await businessService?.upsertBusinessHours(editedBusiness?.id, businessHours);
        }

        // Exit edit mode
        setIsEditable(false);
        
        // Call parent callback with updated data
        if (onSave) {
          onSave(data || editedBusiness);
        }
        
        // Show success message
        console.log('Business listing updated successfully!');
      } else {
        console.error('Error updating business:', error);
        alert('Failed to update business listing. Please try again.');
      }
    } catch (error) {
      console.error('Error saving business:', error);
      alert('An error occurred while saving. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset to original business data
    if (business) {
      setEditedBusiness({ ...business });
    }
    
    // Exit edit mode
    setIsEditable(false);
    
    // Call parent callback if provided
    if (onCancel) {
      onCancel();
    }
  };

  const toggleServiceExpansion = (index) => {
    const newExpanded = new Set(expandedServices);
    if (newExpanded?.has(index)) {
      newExpanded?.delete(index);
    } else {
      newExpanded?.add(index);
    }
    setExpandedServices(newExpanded);
  };

  const handleEditService = (service, index) => {
    setEditingService(index);
    setNewService({
      id: service?.id,
      name: service?.name || '',
      description: service?.description || '',
      price: service?.price || '',
      duration_minutes: service?.duration_minutes || '',
      is_in_person: service?.is_in_person ?? true,
      is_online: service?.is_online ?? false,
      max_participants: service?.max_participants || 1
    });
  };

  const handleSaveService = async () => {
    try {
      if (editingService !== null && editingService !== -1) {
        // Update existing service
        const { error } = await businessService?.updateService(newService?.id, {
          name: newService?.name,
          description: newService?.description,
          price: newService?.price,
          duration_minutes: newService?.duration_minutes,
          is_in_person: newService?.is_in_person,
          is_online: newService?.is_online,
          max_participants: newService?.max_participants
        });

        if (!error) {
          // Update local state
          const updatedServices = [...services];
          updatedServices[editingService] = { ...services?.[editingService], ...newService };
          setServices(updatedServices);
        }
      } else {
        // Add new service
        const { data, error } = await businessService?.createService(business?.id, newService);
        
        if (!error && data) {
          setServices(prev => [...prev, data]);
        }
      }

      // Reset form
      setEditingService(null);
      setNewService({
        name: '',
        description: '',
        price: '',
        duration_minutes: '',
        is_in_person: true,
        is_online: false,
        max_participants: 1
      });
    } catch (error) {
      console.error('Error saving service:', error);
      alert('Failed to save service. Please try again.');
    }
  };

  const handleDeleteService = async (serviceId, index) => {
    if (confirm('Are you sure you want to delete this service?')) {
      try {
        const { error } = await businessService?.deleteService(serviceId);
        
        if (!error) {
          const updatedServices = services?.filter((_, i) => i !== index);
          setServices(updatedServices);
        } else {
          alert('Failed to delete service. Please try again.');
        }
      } catch (error) {
        console.error('Error deleting service:', error);
        alert('Failed to delete service. Please try again.');
      }
    }
  };

  const handleCancelServiceEdit = () => {
    setEditingService(null);
    setNewService({
      name: '',
      description: '',
      price: '',
      duration_minutes: '',
      is_in_person: true,
      is_online: false,
      max_participants: 1
    });
  };

  const handleBusinessHourChange = (dayOfWeek, field, value) => {
    setBusinessHours(prev => {
      const existingIndex = prev?.findIndex(h => h?.day_of_week === dayOfWeek);
      
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = { ...updated?.[existingIndex], [field]: value };
        return updated;
      } else {
        return [...prev, {
          day_of_week: dayOfWeek,
          [field]: value,
          is_closed: false
        }];
      }
    });
  };

  const getDayHours = (dayOfWeek) => {
    return businessHours?.find(h => h?.day_of_week === dayOfWeek) || {
      day_of_week: dayOfWeek,
      open_time: '09:00',
      close_time: '17:00',
      is_closed: false
    };
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr?.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getFullAddress = () => {
    const parts = [
      editedBusiness?.address_line1,
      editedBusiness?.address_line2,
      editedBusiness?.city,
      editedBusiness?.county,
      editedBusiness?.postcode
    ]?.filter(Boolean);
    return parts?.join(', ');
  };

  const canAddServices = subscriptionData?.canAddServices || subscriptionData?.subscription_tier !== 'basic';

  // Chat functionality
  const handleStartChat = async () => {
    if (!user?.id || !business?.owner_id) {
      alert('Please sign in to start a conversation');
      return;
    }

    if (user?.id === business?.owner_id) {
      alert('You cannot chat with yourself');
      return;
    }

    try {
      setChatLoading(true);
      
      // Create conversation with business owner
      const { data: conversation, error } = await messagingService?.createConversation(
        user?.id, // client_id (current user)
        business?.owner_id, // practitioner_id (business owner)
        `Inquiry about ${business?.name}` // subject
      );

      if (error) {
        alert(`Failed to start conversation: ${error}`);
        return;
      }

      // Show chat modal or navigate to messages
      setShowChatModal(true);
    } catch (error) {
      alert('Failed to start conversation. Please try again.');
    } finally {
      setChatLoading(false);
    }
  };

  const canStartChat = () => {
    return user?.id && business?.owner_id && user?.id !== business?.owner_id;
  };

  if (!business) {
    return (
      <div className="text-center py-12">
        <Icon name="Building" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">
          No Business Found
        </h3>
        <p className="text-muted-foreground mb-4">
          No business listing available to display or edit.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Edit Controls */}
      <div className="px-6 py-4 bg-muted/30 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="Building" size={20} className="text-primary" />
            <h2 className="text-lg font-semibold text-foreground">
              Business Listing
            </h2>
            {isEditable && (
              <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                Editing Mode
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {isEditable ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSave}
                  disabled={isSaving}
                  iconName="Save"
                  iconPosition="left"
                  iconSize={14}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={handleToggleEdit}
                iconName="Edit"
                iconPosition="left"
                iconSize={14}
              >
                Edit Listing
              </Button>
            )}
          </div>
        </div>
      </div>
      <div className="p-6">
        {/* Business Header */}
        <div className="relative mb-8">
          {/* Header Image */}
          <div className="relative h-48 md:h-64 overflow-hidden rounded-lg bg-muted">
            {editedBusiness?.cover_image_url ? (
              <Image
                src={editedBusiness?.cover_image_url}
                alt={`${editedBusiness?.name} header`}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Icon name="Image" size={48} className="text-muted-foreground" />
              </div>
            )}
          </div>

          {/* Business Info */}
          <div className="mt-6">
            <div className="flex items-start space-x-4 mb-4">
              {/* Logo */}
              <div className="w-16 h-16 rounded-lg overflow-hidden border-2 border-border bg-white flex-shrink-0">
                {editedBusiness?.logo_url ? (
                  <Image
                    src={editedBusiness?.logo_url}
                    alt={`${editedBusiness?.name} logo`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Icon name="Building" size={24} className="text-muted-foreground" />
                  </div>
                )}
              </div>

              <div className="flex-1">
                {/* Business Name */}
                {isEditable ? (
                  <Input
                    value={editedBusiness?.name || ''}
                    onChange={(e) => handleInputChange('name', e?.target?.value)}
                    placeholder="Business name"
                    className="text-2xl font-semibold mb-2"
                  />
                ) : (
                  <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground mb-2">
                    {editedBusiness?.name}
                  </h1>
                )}

                {/* Enhanced Star Rating Display - Prominent Location */}
                {editedBusiness?.google_rating && (
                  <div className="mb-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <StarRating 
                          rating={editedBusiness?.google_rating}
                          size={20}
                          showCount={true}
                          reviewCount={editedBusiness?.google_review_count}
                          className="mb-1"
                        />
                      </div>
                      {editedBusiness?.google_my_business_url && (
                        <a 
                          href={editedBusiness?.google_my_business_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-yellow-700 dark:text-yellow-300 hover:text-yellow-900 dark:hover:text-yellow-100 font-medium flex items-center space-x-1"
                        >
                          <Icon name="ExternalLink" size={12} />
                          <span>Google Reviews</span>
                        </a>
                      )}
                    </div>
                    <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                      Verified Google My Business rating
                    </p>
                  </div>
                )}

                {/* Categories - show category name if available */}
                {editedBusiness?.categories?.name && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full">
                      {editedBusiness?.categories?.name}
                    </span>
                  </div>
                )}

                {/* Short Description */}
                {isEditable ? (
                  <textarea
                    value={editedBusiness?.short_description || ''}
                    onChange={(e) => handleInputChange('short_description', e?.target?.value)}
                    placeholder="Short description"
                    rows={2}
                    className="w-full px-3 py-2 border border-border rounded-lg resize-none text-sm"
                  />
                ) : (
                  <p className="text-muted-foreground text-sm">
                    {editedBusiness?.short_description}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Reviews & Ratings Section */}
        {(editedBusiness?.google_rating || isEditable) && (
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-lg p-6 mb-8 border border-yellow-200 dark:border-yellow-800">
            <h3 className="font-semibold text-foreground mb-4 flex items-center space-x-2">
              <Icon name="Star" size={20} className="text-yellow-500" />
              <span>Customer Reviews & Ratings</span>
              {hasGoogleMyBusiness() && (
                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full flex items-center space-x-1">
                  <Icon name="CheckCircle" size={12} />
                  <span>Verified</span>
                </span>
              )}
            </h3>
            
            {editedBusiness?.google_rating ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Main Rating Display */}
                <div className="text-center md:text-left">
                  <div className="flex items-center justify-center md:justify-start space-x-2 mb-2">
                    <span className="text-4xl font-bold text-yellow-600 dark:text-yellow-400">
                      {editedBusiness?.google_rating?.toFixed(1)}
                    </span>
                    <div className="flex flex-col">
                      <StarRating 
                        rating={editedBusiness?.google_rating}
                        size={18}
                        className="mb-1"
                      />
                      <span className="text-sm text-muted-foreground">
                        out of 5 stars
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Google My Business Rating
                  </p>
                </div>

                {/* Review Count */}
                <div className="text-center md:text-left">
                  <div className="flex items-center justify-center md:justify-start space-x-2 mb-2">
                    <Icon name="Users" size={24} className="text-primary" />
                    <span className="text-2xl font-semibold text-foreground">
                      {editedBusiness?.google_review_count || 0}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Total Reviews
                  </p>
                </div>

                {/* Rating Breakdown Visualization */}
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-foreground mb-3">Rating Breakdown</h4>
                  {[5, 4, 3, 2, 1]?.map(stars => {
                    // This would ideally come from API data, for now we'll show a placeholder
                    const percentage = stars === Math.floor(editedBusiness?.google_rating) ? 70 : 10;
                    return (
                      <div key={stars} className="flex items-center space-x-2 text-xs">
                        <span className="w-3 text-center">{stars}</span>
                        <Icon name="Star" size={12} className="text-yellow-500" />
                        <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div 
                            className="bg-yellow-500 h-2 rounded-full" 
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className="w-8 text-right text-muted-foreground">{percentage}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <Icon name="Star" size={48} className="text-yellow-300 mx-auto mb-4" />
                <h4 className="font-medium text-foreground mb-2">
                  No Ratings Yet
                </h4>
                <p className="text-muted-foreground text-sm mb-4">
                  {isEditing 
                    ? hasGoogleMyBusiness()
                      ? "Your Google My Business ratings will appear here automatically once customers start leaving reviews." :"Connect your Google My Business listing to display customer ratings and reviews." :"This business doesn't have any ratings to display yet."
                  }
                </p>
                {isEditing && !hasGoogleMyBusiness() && (
                  <div className="text-xs text-yellow-700 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/30 p-3 rounded-lg">
                    <p className="font-medium mb-1">💡 Enable Automatic Star Ratings:</p>
                    <p>Connect your Google My Business listing in the "Location & Map" section below to automatically display your real customer ratings and reviews.</p>
                  </div>
                )}
              </div>
            )}

            {/* Quick Actions for Reviews */}
            {editedBusiness?.google_my_business_url && (
              <div className="mt-6 pt-4 border-t border-yellow-200 dark:border-yellow-700">
                <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                  <a 
                    href={editedBusiness?.google_my_business_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-white dark:bg-gray-800 border border-yellow-300 dark:border-yellow-600 rounded-lg hover:bg-yellow-50 dark:hover:bg-yellow-900/20 transition-gentle text-sm font-medium"
                  >
                    <Icon name="ExternalLink" size={14} />
                    <span>View All Reviews</span>
                  </a>
                  <a 
                    href={`https://search.google.com/local/writereview?placeid=${editedBusiness?.google_places_id || ''}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg transition-gentle text-sm font-medium"
                  >
                    <Icon name="Star" size={14} />
                    <span>Write a Review</span>
                  </a>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Contact Information */}
        <div className="bg-muted/30 rounded-lg p-6 mb-8">
          <h3 className="font-semibold text-foreground mb-6 flex items-center space-x-2">
            <Icon name="Contact" size={20} className="text-primary" />
            <span>Contact Information</span>
          </h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Phone */}
            <div className="flex flex-col space-y-2">
              <div className="flex items-center space-x-2 text-muted-foreground">
                <Icon name="Phone" size={16} />
                <span className="text-sm font-medium">Phone</span>
              </div>
              {isEditable ? (
                <Input
                  value={editedBusiness?.phone || ''}
                  onChange={(e) => handleInputChange('phone', e?.target?.value)}
                  placeholder="Phone number"
                  className="text-sm"
                />
              ) : editedBusiness?.phone ? (
                <a 
                  href={`tel:${editedBusiness?.phone}`}
                  className="text-sm text-foreground hover:text-primary transition-gentle font-medium"
                >
                  {editedBusiness?.phone}
                </a>
              ) : (
                <span className="text-sm text-muted-foreground italic">Not provided</span>
              )}
            </div>

            {/* Email */}
            <div className="flex flex-col space-y-2">
              <div className="flex items-center space-x-2 text-muted-foreground">
                <Icon name="Mail" size={16} />
                <span className="text-sm font-medium">Email</span>
              </div>
              {isEditable ? (
                <Input
                  value={editedBusiness?.email || ''}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                  placeholder="Email address"
                  className="text-sm"
                />
              ) : editedBusiness?.email ? (
                <a 
                  href={`mailto:${editedBusiness?.email}`}
                  className="text-sm text-foreground hover:text-primary transition-gentle font-medium break-all"
                >
                  {editedBusiness?.email}
                </a>
              ) : (
                <span className="text-sm text-muted-foreground italic">Not provided</span>
              )}
            </div>

            {/* Website */}
            <div className="flex flex-col space-y-2">
              <div className="flex items-center space-x-2 text-muted-foreground">
                <Icon name="Globe" size={16} />
                <span className="text-sm font-medium">Website</span>
              </div>
              {isEditable ? (
                <Input
                  value={editedBusiness?.website_url || ''}
                  onChange={(e) => handleInputChange('website_url', e?.target?.value)}
                  placeholder="Website URL"
                  className="text-sm"
                />
              ) : editedBusiness?.website_url ? (
                <a 
                  href={editedBusiness?.website_url?.startsWith('http') ? editedBusiness?.website_url : `https://${editedBusiness?.website_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-foreground hover:text-primary transition-gentle font-medium break-all"
                >
                  {editedBusiness?.website_url?.replace(/^https?:\/\//, '')}
                </a>
              ) : (
                <span className="text-sm text-muted-foreground italic">Not provided</span>
              )}
            </div>
          </div>

          {/* Quick Location Preview */}
          {(editedBusiness?.city || editedBusiness?.county || editedBusiness?.postcode) && (
            <div className="mt-6 pt-4 border-t border-border">
              <div className="flex items-start space-x-3">
                <Icon name="MapPin" size={16} className="text-primary mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Location</p>
                  <p className="text-sm text-muted-foreground">
                    {[editedBusiness?.city, editedBusiness?.county, editedBusiness?.postcode]?.filter(Boolean)?.join(', ')}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Description */}
        <div className="mb-8">
          <h3 className="font-semibold text-foreground mb-4 flex items-center space-x-2">
            <Icon name="FileText" size={18} />
            <span>About</span>
          </h3>
          
          {isEditable ? (
            <textarea
              value={editedBusiness?.description || ''}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
              placeholder="Detailed business description"
              rows={6}
              className="w-full px-3 py-2 border border-border rounded-lg resize-none text-sm"
            />
          ) : (
            <div className="text-foreground text-sm leading-relaxed whitespace-pre-wrap">
              {editedBusiness?.description}
            </div>
          )}
        </div>

        {/* What We Offer Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-foreground flex items-center space-x-2">
              <Icon name="Package" size={18} />
              <span>What We Offer</span>
            </h3>
            
            {isEditable && canAddServices && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditingService(-1)}
                iconName="Plus"
                iconPosition="left"
                iconSize={14}
              >
                Add Service
              </Button>
            )}
          </div>

          {/* Service Form Modal */}
          {(editingService !== null) && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-card rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <h4 className="font-semibold text-foreground mb-4">
                  {editingService === -1 ? 'Add New Service' : 'Edit Service'}
                </h4>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Service Name"
                      value={newService?.name}
                      onChange={(e) => setNewService(prev => ({ ...prev, name: e?.target?.value }))}
                      placeholder="e.g. Reiki Healing"
                    />
                    
                    <Input
                      label="Price (£)"
                      type="number"
                      value={newService?.price}
                      onChange={(e) => setNewService(prev => ({ ...prev, price: e?.target?.value }))}
                      placeholder="65"
                    />
                  </div>

                  <Input
                    label="Duration (minutes)"
                    type="number"
                    value={newService?.duration_minutes}
                    onChange={(e) => setNewService(prev => ({ ...prev, duration_minutes: parseInt(e?.target?.value) || '' }))}
                    placeholder="60"
                  />

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Description
                    </label>
                    <textarea
                      value={newService?.description}
                      onChange={(e) => setNewService(prev => ({ ...prev, description: e?.target?.value }))}
                      placeholder="Detailed service description..."
                      rows={4}
                      className="w-full px-3 py-2 border border-border rounded-lg resize-none"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Max Participants
                      </label>
                      <Input
                        type="number"
                        value={newService?.max_participants}
                        onChange={(e) => setNewService(prev => ({ ...prev, max_participants: parseInt(e?.target?.value) || 1 }))}
                        min="1"
                      />
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="is_in_person"
                        checked={newService?.is_in_person}
                        onChange={(e) => setNewService(prev => ({ ...prev, is_in_person: e?.target?.checked }))}
                        className="rounded border-border"
                      />
                      <label htmlFor="is_in_person" className="text-sm">In Person</label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="is_online"
                        checked={newService?.is_online}
                        onChange={(e) => setNewService(prev => ({ ...prev, is_online: e?.target?.checked }))}
                        className="rounded border-border"
                      />
                      <label htmlFor="is_online" className="text-sm">Online</label>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-2 mt-6">
                  <Button
                    variant="ghost"
                    onClick={handleCancelServiceEdit}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    onClick={handleSaveService}
                    disabled={!newService?.name?.trim()}
                  >
                    {editingService === -1 ? 'Add Service' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Services Grid */}
          {services?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {services?.map((service, index) => {
                const isExpanded = expandedServices?.has(index);
                const shouldTruncate = service?.description?.length > 150;
                const displayDescription = shouldTruncate && !isExpanded
                  ? service?.description?.substring(0, 150) + '...'
                  : service?.description;

                return (
                  <div
                    key={service?.id}
                    className="border border-border rounded-lg p-4 hover:shadow-organic transition-organic relative"
                  >
                    {isEditable && (
                      <div className="absolute top-2 right-2 flex space-x-1">
                        <button
                          onClick={() => handleEditService(service, index)}
                          className="p-1 text-muted-foreground hover:text-primary rounded"
                        >
                          <Icon name="Edit" size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteService(service?.id, index)}
                          className="p-1 text-muted-foreground hover:text-destructive rounded"
                        >
                          <Icon name="Trash" size={14} />
                        </button>
                      </div>
                    )}

                    <div className="flex items-start justify-between mb-3 pr-8">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                          <Icon name="Star" size={20} className="text-primary" />
                        </div>
                        <div>
                          <h4 className="font-medium text-foreground">
                            {service?.name}
                          </h4>
                          {service?.duration_minutes && (
                            <p className="text-sm text-muted-foreground">
                              {service?.duration_minutes} minutes
                            </p>
                          )}
                        </div>
                      </div>
                      {service?.price && (
                        <span className="text-primary font-semibold">
                          £{service?.price}
                        </span>
                      )}
                    </div>

                    <p className="text-foreground text-sm leading-relaxed mb-3">
                      {displayDescription}
                    </p>

                    {shouldTruncate && (
                      <button
                        onClick={() => toggleServiceExpansion(index)}
                        className="text-primary hover:text-primary/80 text-sm font-medium transition-gentle"
                      >
                        {isExpanded ? 'Show less' : 'Read more'}
                      </button>
                    )}

                    {/* Service Details */}
                    <div className="mt-3 pt-3 border-t border-border">
                      <div className="flex flex-wrap gap-2">
                        {service?.is_in_person && (
                          <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded">
                            In Person
                          </span>
                        )}
                        {service?.is_online && (
                          <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded">
                            Online
                          </span>
                        )}
                        {service?.max_participants && service?.max_participants > 1 && (
                          <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded">
                            Max {service?.max_participants} people
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 border-2 border-dashed border-border rounded-lg mb-8">
              <Icon name="Package" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h4 className="font-medium text-foreground mb-2">
                No Services Listed
              </h4>
              <p className="text-muted-foreground text-sm mb-4">
                {isEditing 
                  ? canAddServices 
                    ? "Start adding services to showcase what you offer to potential clients." :"Upgrade to premium to add services to your business profile." :"This business hasn't listed any services yet."
                }
              </p>
              {isEditing && canAddServices && (
                <Button
                  variant="default"
                  onClick={() => setEditingService(-1)}
                  iconName="Plus"
                  iconPosition="left"
                  iconSize={16}
                >
                  Add Your First Service
                </Button>
              )}
              {isEditing && !canAddServices && (
                <Button
                  variant="default"
                  onClick={() => navigate('/add-service')}
                  iconName="Crown"
                  iconPosition="left"
                  iconSize={16}
                >
                  Upgrade for £9.99/year
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Enhanced map display logic for Google My Business integration */}
        <div className="bg-muted/30 rounded-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h4 className="font-semibold text-foreground flex items-center space-x-2">
              <Icon name="MapPin" size={20} className="text-primary" />
              <span>Location & Map</span>
              {hasGoogleMyBusiness() && (
                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full flex items-center space-x-1">
                  <Icon name="CheckCircle" size={12} />
                  <span>Google My Business</span>
                </span>
              )}
            </h4>
            {(editedBusiness?.latitude && editedBusiness?.longitude) && (
              <button
                onClick={() => setShowMap(!showMap)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg border transition-gentle text-sm font-medium ${
                  showMap 
                    ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90' 
                    : 'border-border bg-background text-primary hover:bg-primary/10'
                }`}
              >
                <Icon name={showMap ? 'EyeOff' : 'Map'} size={16} />
                <span>{showMap ? 'Hide Map' : 'Show Map'}</span>
              </button>
            )}
          </div>

          {/* Map Not Available Notice - Updated for Google My Business context */}
          {!editedBusiness?.latitude && !editedBusiness?.longitude && !hasGoogleMyBusiness() && (
            <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <div className="flex items-start space-x-3">
                <Icon name="MapPin" size={20} className="text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h5 className="font-medium text-amber-800 dark:text-amber-200 mb-2">
                    Map Display Not Available
                  </h5>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mb-3">
                    To enable map display, you can either add GPS coordinates manually or connect your Google My Business listing for automatic location data.
                  </p>
                  <div className="space-y-2 text-sm">
                    {isEditable ? (
                      <>
                        <div className="flex items-center space-x-2">
                          <Icon name="ArrowDown" size={14} className="text-amber-600 dark:text-amber-400" />
                          <span className="font-medium text-amber-800 dark:text-amber-200">
                            Add coordinates manually in the form below, or
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Icon name="Link" size={14} className="text-amber-600 dark:text-amber-400" />
                          <span className="font-medium text-amber-800 dark:text-amber-200">
                            Connect your Google My Business listing for automatic sync
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <Icon name="Edit" size={14} className="text-amber-600 dark:text-amber-400" />
                        <span className="font-medium text-amber-800 dark:text-amber-200">
                          Click "Edit Listing" to add location data or connect Google My Business
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Enhanced Map Display with Google My Business integration */}
          {showMap && editedBusiness?.latitude && editedBusiness?.longitude && (
            <div className="mb-6">
              <div className="relative h-80 rounded-lg overflow-hidden border border-border shadow-sm">
                {/* Enhanced iframe with Google My Business support */}
                <iframe
                  width="100%"
                  height="100%"
                  loading="lazy"
                  title={`${editedBusiness?.name} location`}
                  referrerPolicy="no-referrer-when-downgrade"
                  src={
                    editedBusiness?.google_places_id 
                      ? `https://www.google.com/maps/embed/v1/place?key=${import.meta.env?.VITE_GOOGLE_MAPS_API_KEY}&q=place_id:${editedBusiness?.google_places_id}&zoom=15`
                      : `https://www.google.com/maps?q=${editedBusiness?.latitude},${editedBusiness?.longitude}&z=15&output=embed`
                  }
                  className="w-full h-full"
                  style={{ border: 0 }}
                />
                
                {/* Enhanced Map Overlay with Google My Business info and Star Rating */}
                <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm rounded-lg p-3 shadow-sm border border-border max-w-xs">
                  <div className="flex items-center space-x-2 mb-1">
                    <Icon name="Building" size={14} className="text-primary" />
                    <span className="font-medium text-foreground text-sm">{editedBusiness?.name}</span>
                    {hasGoogleMyBusiness() && (
                      <Icon name="CheckCircle" size={12} className="text-green-600" title="Google My Business Verified" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">{getFullAddress()}</p>
                  
                  {/* Enhanced Star Rating display in map overlay */}
                  {editedBusiness?.google_rating && (
                    <div className="mb-2">
                      <StarRating 
                        rating={editedBusiness?.google_rating}
                        size={12}
                        showCount={true}
                        reviewCount={editedBusiness?.google_review_count}
                      />
                    </div>
                  )}
                </div>
              </div>
              
              {/* Enhanced coordinate display with Google My Business context */}
              <div className="mt-3 flex items-center justify-center space-x-4 text-xs text-muted-foreground">
                <span className="flex items-center space-x-1">
                  <Icon name="Navigation" size={12} />
                  <span>Lat: {editedBusiness?.latitude?.toFixed(4)}</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Icon name="Navigation" size={12} />
                  <span>Lng: {editedBusiness?.longitude?.toFixed(4)}</span>
                </span>
                {editedBusiness?.google_places_id && (
                  <span className="flex items-center space-x-1">
                    <Icon name="Globe" size={12} />
                    <span>Google Places ID</span>
                  </span>
                )}
                {editedBusiness?.google_rating && (
                  <span className="flex items-center space-x-1">
                    <Icon name="Star" size={12} className="text-yellow-500" />
                    <span>{editedBusiness?.google_rating}/5</span>
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Address Details with Google My Business integration fields */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left column - Address fields */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Street Address
                  </label>
                  {isEditable ? (
                    <Input
                      value={editedBusiness?.address_line1 || ''}
                      onChange={(e) => handleInputChange('address_line1', e?.target?.value)}
                      placeholder="e.g. 123 Wellness Street"
                      className="text-sm"
                    />
                  ) : (
                    <p className="text-sm text-foreground bg-muted/30 rounded p-2">
                      {editedBusiness?.address_line1 || <span className="text-muted-foreground italic">Not provided</span>}
                    </p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Address Line 2 <span className="text-muted-foreground">(Optional)</span>
                  </label>
                  {isEditable ? (
                    <Input
                      value={editedBusiness?.address_line2 || ''}
                      onChange={(e) => handleInputChange('address_line2', e?.target?.value)}
                      placeholder="Apartment, suite, unit, etc."
                      className="text-sm"
                    />
                  ) : editedBusiness?.address_line2 ? (
                    <p className="text-sm text-foreground bg-muted/30 rounded p-2">
                      {editedBusiness?.address_line2}
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">Not specified</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      City
                    </label>
                    {isEditable ? (
                      <Input
                        value={editedBusiness?.city || ''}
                        onChange={(e) => handleInputChange('city', e?.target?.value)}
                        placeholder="City"
                        className="text-sm"
                      />
                    ) : (
                      <p className="text-sm text-foreground bg-muted/30 rounded p-2">
                        {editedBusiness?.city || <span className="text-muted-foreground italic">Not provided</span>}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Postcode
                    </label>
                    {isEditable ? (
                      <Input
                        value={editedBusiness?.postcode || ''}
                        onChange={(e) => handleInputChange('postcode', e?.target?.value)}
                        placeholder="SW1A 1AA"
                        className="text-sm"
                      />
                    ) : (
                      <p className="text-sm text-foreground bg-muted/30 rounded p-2">
                        {editedBusiness?.postcode || <span className="text-muted-foreground italic">Not provided</span>}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Right column - Enhanced with Google My Business fields */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    County
                  </label>
                  {isEditable ? (
                    <Input
                      value={editedBusiness?.county || ''}
                      onChange={(e) => handleInputChange('county', e?.target?.value)}
                      placeholder="County"
                      className="text-sm"
                    />
                  ) : (
                    <p className="text-sm text-foreground bg-muted/30 rounded p-2">
                      {editedBusiness?.county || <span className="text-muted-foreground italic">Not provided</span>}
                    </p>
                  )}
                </div>

                {/* Google My Business Integration Fields - Show in Edit Mode */}
                {isEditable && (
                  <div className="space-y-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <h5 className="font-medium text-blue-800 dark:text-blue-200 mb-3 flex items-center space-x-2">
                      <Icon name="Globe" size={16} />
                      <span>Google My Business Integration</span>
                    </h5>
                    
                    <div>
                      <label className="block text-xs text-blue-700 dark:text-blue-300 mb-1">
                        Google My Business URL
                      </label>
                      <Input
                        value={editedBusiness?.google_my_business_url || ''}
                        onChange={(e) => handleInputChange('google_my_business_url', e?.target?.value)}
                        placeholder="https://business.google.com/dashboard/..."
                        className="text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs text-blue-700 dark:text-blue-300 mb-1">
                        Google Places ID
                      </label>
                      <Input
                        value={editedBusiness?.google_places_id || ''}
                        onChange={(e) => handleInputChange('google_places_id', e?.target?.value)}
                        placeholder="ChIJN1t_tDeuEmsRUsoyG83frY4"
                        className="text-sm"
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="google_sync_enabled"
                        checked={editedBusiness?.google_sync_enabled || false}
                        onChange={(e) => handleInputChange('google_sync_enabled', e?.target?.checked)}
                        className="rounded border-border"
                      />
                      <label htmlFor="google_sync_enabled" className="text-xs text-blue-700 dark:text-blue-300">
                        Enable Google My Business sync
                      </label>
                    </div>

                    <div className="text-xs text-blue-600 dark:text-blue-400 space-y-1">
                      <p className="font-medium">💡 How to connect Google My Business:</p>
                      <ul className="space-y-1 ml-3">
                        <li>• Visit Google My Business and find your listing</li>
                        <li>• Copy the dashboard URL from your browser</li>
                        <li>• Get your Google Places ID from Google Places API</li>
                        <li>• Enable sync to automatically update ratings and reviews</li>
                      </ul>
                    </div>
                  </div>
                )}

                {/* GPS Coordinates - Show in Edit Mode */}
                {isEditable && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-3">
                      GPS Coordinates <span className="text-muted-foreground">(For map display)</span>
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-muted-foreground mb-1">
                          Latitude
                        </label>
                        <Input
                          type="number"
                          step="any"
                          value={editedBusiness?.latitude || ''}
                          onChange={(e) => handleInputChange('latitude', parseFloat(e?.target?.value) || null)}
                          placeholder="51.5074"
                          className="text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs text-muted-foreground mb-1">
                          Longitude
                        </label>
                        <Input
                          type="number"
                          step="any"
                          value={editedBusiness?.longitude || ''}
                          onChange={(e) => handleInputChange('longitude', parseFloat(e?.target?.value) || null)}
                          placeholder="-0.1278"
                          className="text-sm"
                        />
                      </div>
                    </div>
                    <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <div className="flex items-start space-x-2">
                        <Icon name="Info" size={14} className="text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-blue-700 dark:text-blue-300">
                          <p className="font-medium mb-1">💡 Coordinate options:</p>
                          <ul className="space-y-1">
                            <li>• Manual: Find coordinates on Google Maps</li>
                            <li>• Google My Business: Coordinates sync automatically</li>
                            <li>• Right-click your location on Google Maps to get coordinates</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Enhanced Contact Quick Actions - Show in View Mode */}
                {!isEditable && (
                  <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
                    <h5 className="font-medium text-foreground mb-3 text-sm">Quick Actions</h5>
                    <div className="space-y-2">
                      {editedBusiness?.phone && (
                        <a 
                          href={`tel:${editedBusiness?.phone}`}
                          className="flex items-center space-x-2 text-sm text-primary hover:text-primary/80 transition-gentle"
                        >
                          <Icon name="Phone" size={14} />
                          <span>Call {editedBusiness?.phone}</span>
                        </a>
                      )}
                      
                      {editedBusiness?.email && (
                        <a 
                          href={`mailto:${editedBusiness?.email}`}
                          className="flex items-center space-x-2 text-sm text-primary hover:text-primary/80 transition-gentle"
                        >
                          <Icon name="Mail" size={14} />
                          <span>Send Email</span>
                        </a>
                      )}
                      
                      {canStartChat() && (
                        <button
                          onClick={handleStartChat}
                          disabled={chatLoading}
                          className="flex items-center space-x-2 text-sm text-primary hover:text-primary/80 transition-gentle disabled:opacity-50"
                        >
                          <Icon name="MessageSquare" size={14} />
                          <span>{chatLoading ? 'Starting chat...' : 'Start Chat'}</span>
                        </button>
                      )}
                      
                      {editedBusiness?.latitude && editedBusiness?.longitude && (
                        <a 
                          href={`https://www.google.com/maps/dir/?api=1&destination=${editedBusiness?.latitude},${editedBusiness?.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-2 text-sm text-primary hover:text-primary/80 transition-gentle"
                        >
                          <Icon name="Navigation" size={14} />
                          <span>Get Directions</span>
                        </a>
                      )}

                      {/* Enhanced Google My Business quick action */}
                      {editedBusiness?.google_my_business_url ? (
                        <a 
                          href={editedBusiness?.google_my_business_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-2 text-sm text-primary hover:text-primary/80 transition-gentle"
                        >
                          <Icon name="ExternalLink" size={14} />
                          <span>View on Google My Business</span>
                        </a>
                      ) : editedBusiness?.website_url && (
                        <a 
                          href={editedBusiness?.website_url?.startsWith('http') ? editedBusiness?.website_url : `https://${editedBusiness?.website_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-2 text-sm text-primary hover:text-primary/80 transition-gentle"
                        >
                          <Icon name="ExternalLink" size={14} />
                          <span>Visit Website</span>
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Opening Hours Section */}
        <div className="bg-muted/30 rounded-lg p-6">
          <h4 className="font-semibold text-foreground mb-4 flex items-center space-x-2">
            <Icon name="Clock" size={18} />
            <span>Opening Hours</span>
          </h4>

          {/* Responsive Horizontal/Stacked Layout */}
          <div className="flex flex-col lg:flex-row lg:flex-wrap lg:gap-x-6 gap-y-3">
            {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']?.map(day => {
              const dayHours = getDayHours(day);
              
              return (
                <div key={day} className="flex items-center justify-between lg:justify-start lg:w-auto w-full py-2 border-b border-border last:border-b-0">
                  <div className="flex items-center lg:flex-col lg:items-start space-x-3 lg:space-x-0 lg:space-y-1 min-w-0 flex-1 lg:flex-none">
                    <span className="capitalize font-medium text-foreground w-20 lg:w-auto flex-shrink-0 text-sm lg:text-base">
                      {day?.charAt(0)?.toUpperCase() + day?.slice(1, 3)}
                      <span className="hidden lg:inline">{day?.slice(3)}</span>
                    </span>
                    
                    {isEditable ? (
                      <div className="flex items-center space-x-2 lg:space-x-1 flex-1 lg:flex-none">
                        <label className="flex items-center space-x-1 lg:space-x-2">
                          <input
                            type="checkbox"
                            checked={dayHours?.is_closed}
                            onChange={(e) => handleBusinessHourChange(day, 'is_closed', e?.target?.checked)}
                            className="rounded border-border"
                          />
                          <span className="text-xs lg:text-sm">Closed</span>
                        </label>
                        
                        {!dayHours?.is_closed && (
                          <div className="flex flex-col lg:flex-row items-center space-y-1 lg:space-y-0 lg:space-x-1 flex-1 lg:flex-none">
                            <input
                              type="time"
                              value={dayHours?.open_time || '09:00'}
                              onChange={(e) => handleBusinessHourChange(day, 'open_time', e?.target?.value)}
                              className="px-1 py-1 border border-border rounded text-xs lg:text-sm w-full lg:w-auto"
                            />
                            <span className="text-xs lg:text-sm text-muted-foreground hidden lg:inline">to</span>
                            <input
                              type="time"
                              value={dayHours?.close_time || '17:00'}
                              onChange={(e) => handleBusinessHourChange(day, 'close_time', e?.target?.value)}
                              className="px-1 py-1 border border-border rounded text-xs lg:text-sm w-full lg:w-auto"
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs lg:text-sm text-muted-foreground font-medium lg:font-normal">
                        {dayHours?.is_closed ? 'Closed' : 
                         dayHours?.open_time && dayHours?.close_time ?
                         `${formatTime(dayHours?.open_time)} - ${formatTime(dayHours?.close_time)}` :
                         'Hours not set'
                        }
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Mobile-only divider line after hours */}
          <div className="lg:hidden mt-3 pt-3 border-t border-border">
            <p className="text-xs text-muted-foreground text-center">
              Opening times shown above
            </p>
          </div>
        </div>
      </div>
      {/* Chat Modal */}
      {showChatModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-foreground flex items-center space-x-2">
                <Icon name="MessageSquare" size={20} className="text-primary" />
                <span>Chat Started!</span>
              </h4>
              <button
                onClick={() => setShowChatModal(false)}
                className="p-1 text-muted-foreground hover:text-foreground rounded"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-primary/10 flex items-center justify-center flex-shrink-0">
                  {editedBusiness?.logo_url ? (
                    <Image
                      src={editedBusiness?.logo_url}
                      alt={`${editedBusiness?.name} logo`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Icon name="Building" size={20} className="text-primary" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-foreground text-sm">
                    {editedBusiness?.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Conversation started about this business
                  </p>
                </div>
              </div>

              <p className="text-sm text-muted-foreground">
                Your conversation has been started! You can now exchange messages with the business owner about their services.
              </p>

              <div className="flex space-x-2 pt-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowChatModal(false)}
                  className="flex-1"
                >
                  Continue Browsing
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => {
                    setShowChatModal(false);
                    navigate('/user-profile');
                  }}
                  iconName="MessageSquare"
                  iconPosition="left"
                  iconSize={14}
                  className="flex-1"
                >
                  View Messages
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditableBusinessListing;